import { lazy, ComponentType } from 'react';

interface RetryOptions {
  maxRetries?: number;
  delay?: number;
  exponentialBackoff?: boolean;
}

/**
 * Creates a lazy component with intelligent retry mechanism for dynamic imports
 * Handles cache invalidation and MIME type errors by retrying failed imports
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): React.LazyExoticComponent<T> {
  const { maxRetries = 3, delay = 1000, exponentialBackoff = true } = options;

  const retryImport = async (attempt = 0): Promise<{ default: T }> => {
    try {
      const result = await importFn();
      return result;
    } catch (error) {
      console.warn(`Dynamic import failed (attempt ${attempt + 1}/${maxRetries + 1}):`, error);
      
      // Check if it's a MIME type or module script error
      const isCacheError = error instanceof TypeError && 
        (error.message.includes('Failed to fetch') || 
         error.message.includes('MIME type') ||
         error.message.includes('module script'));

      if (isCacheError && attempt < maxRetries) {
        // Calculate delay with exponential backoff
        const retryDelay = exponentialBackoff ? delay * Math.pow(2, attempt) : delay;
        
        console.log(`Retrying dynamic import in ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        
        return retryImport(attempt + 1);
      }
      
      // If all retries failed and it's a cache issue, force a full page reload
      if (isCacheError && attempt >= maxRetries) {
        console.error('All retry attempts failed. Reloading page to clear cache...');
        window.location.reload();
        // This will never resolve, but TypeScript needs a return
        return new Promise(() => {}) as Promise<{ default: T }>;
      }
      
      throw error;
    }
  };

  return lazy(() => retryImport());
}

/**
 * Check if current session is stale (user has been away for a long time)
 * and proactively handle cache issues
 */
export function detectStaleSession(): boolean {
  const lastActivity = localStorage.getItem('last-activity');
  if (!lastActivity) {
    localStorage.setItem('last-activity', Date.now().toString());
    return false;
  }

  const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
  const staleThreshold = 30 * 60 * 1000; // 30 minutes

  if (timeSinceLastActivity > staleThreshold) {
    console.log('Stale session detected, cache may be invalid');
    return true;
  }

  return false;
}

/**
 * Update last activity timestamp
 */
export function updateActivityTimestamp(): void {
  localStorage.setItem('last-activity', Date.now().toString());
}

/**
 * Clear potentially stale cache data
 */
export function clearStaleCache(): void {
  // Clear service worker caches
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        if (cacheName.includes('fritidskompassen') || cacheName.includes('v')) {
          console.log('Clearing potentially stale cache:', cacheName);
          caches.delete(cacheName);
        }
      });
    });
  }
}